import PropTypes from 'prop-types';
import { formatRetainedDestination } from '../helpers';

function retainedMessage(attempt) {
  if (attempt.orderId) {
    return 'Your order is complete. Loading the order confirmation now.';
  }

  if (attempt.missingQuotedEvidence) {
    return 'Payment was confirmed, but its retained tax quote details could not be loaded. Contact Retainer Club support; do not place the order again.';
  }

  if (attempt.status === 'paid' && attempt.recoveryMode === 'legacy') {
    return attempt.legacyValuesFrozen
      ? 'Legacy payment confirmed. We are finishing the order with the shipping details retained when recovery started.'
      : 'Legacy payment confirmed. Confirm shipping and delivery below to finish this order without another charge.';
  }

  if (attempt.status === 'paid') {
    return 'Payment confirmed. We are finishing the order with the retained quoted shipping details without another charge.';
  }

  if (attempt.status === 'reconciliation_required') {
    return 'Payment status requires support review. Checkout remains locked and a new charge cannot be created.';
  }

  if (attempt.status === 'pending') {
    return 'Payment is still processing. Checkout remains locked while we check the existing attempt; a new charge cannot be created.';
  }

  return 'We are checking the retained payment attempt. A new charge will not be created.';
}

function CheckoutRetainedAttemptStatus({
  onRetry,
  recoveryError = null,
  recoveryLoading = false,
  retainedAttempt = null,
}) {
  if (recoveryLoading && !retainedAttempt) {
    return (
      <p
        className="border-gray-200 border-t px-4 py-3 text-gray-600 text-xs sm:px-6"
        aria-live="polite"
        role="status"
      >
        Checking for an existing payment attempt&hellip;
      </p>
    );
  }

  if (recoveryError && !retainedAttempt) {
    return (
      <div
        className="border-gray-200 border-t px-4 py-3 text-red-700 text-xs sm:px-6"
        role="alert"
      >
        <p>
          We could not check for an existing payment attempt. Checkout is paused
          to prevent a duplicate charge.
        </p>
        <button
          type="button"
          className="mt-2 font-medium underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-red-700 focus-visible:outline-offset-2"
          onClick={onRetry}
        >
          Retry payment status
        </button>
      </div>
    );
  }

  if (!retainedAttempt) {
    return null;
  }

  const destination = formatRetainedDestination(
    retainedAttempt.shippingAddress,
  );
  const canRefresh =
    !retainedAttempt.orderId &&
    ['pending', 'ambiguous'].includes(retainedAttempt.status);

  return (
    <div
      className="border-gray-200 border-t px-4 py-3 text-gray-600 text-xs sm:px-6"
      aria-live="polite"
      role={retainedAttempt.missingQuotedEvidence ? 'alert' : 'status'}
    >
      <p>{retainedMessage(retainedAttempt)}</p>
      {destination ? (
        <p className="mt-2">
          <span className="font-medium text-gray-900">
            Retained destination:
          </span>{' '}
          {destination}
        </p>
      ) : null}
      {retainedAttempt.deliveryMethod ? (
        <p>
          <span className="font-medium text-gray-900">Retained delivery:</span>{' '}
          {retainedAttempt.deliveryMethod}
        </p>
      ) : null}
      {canRefresh ? (
        <button
          type="button"
          className="mt-2 font-medium text-rcprimary-500 underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-rcprimary-500 focus-visible:outline-offset-2"
          onClick={onRetry}
          disabled={recoveryLoading}
        >
          {recoveryLoading
            ? 'Checking payment status…'
            : 'Check payment status'}
        </button>
      ) : null}
    </div>
  );
}

CheckoutRetainedAttemptStatus.propTypes = {
  onRetry: PropTypes.func.isRequired,
  recoveryError: PropTypes.object,
  recoveryLoading: PropTypes.bool,
  retainedAttempt: PropTypes.shape({
    deliveryMethod: PropTypes.string,
    legacyValuesFrozen: PropTypes.bool,
    missingQuotedEvidence: PropTypes.bool,
    orderId: PropTypes.string,
    recoveryMode: PropTypes.string,
    shippingAddress: PropTypes.object,
    status: PropTypes.string.isRequired,
  }),
};

export default CheckoutRetainedAttemptStatus;
